import { TrendsResult } from 'api/useAnalytics';
import { DATE_DESCRIPTIVE_FORMAT } from 'constants/constants';
import { SortingDirection } from 'constants/enums';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import sortBy from 'lodash.sortby';
import { EStatisticType } from 'views/Statistics/StatisticsView.types';

import { ChartData } from './analyticsChart.config';

export enum BreakdownSortField {
  VALUE = 'value',
  BREAKDOWN_VALUE = 'breakdownValue'
}

export interface BreakdownSortConfig {
  field: BreakdownSortField;
  direction: SortingDirection;
}

export const CHART_COLORS = [
  'var(--base-chart-1)',
  'var(--base-chart-2)',
  'var(--base-chart-3)',
  'var(--base-chart-4)',
  'var(--base-chart-5)',
  'var(--base-chart-6)',
  'var(--base-chart-7)',
  'var(--base-chart-8)',
  'var(--base-chart-9)',
  'var(--base-chart-10)',
  'var(--base-chart-11)',
  'var(--base-chart-12)',
  'var(--base-chart-13)',
  'var(--base-chart-14)'
] as const;

export const getFormattedTrendsData = (
  currentTrends: TrendsResult['values'],
  previousTrends?: TrendsResult['values']
): ChartData[] => {
  const currentTrendsSortedByDate = sortBy(currentTrends, 'date');
  const previousTrendsSortedByDate = previousTrends
    ? sortBy(previousTrends, 'date')
    : undefined;
  return currentTrendsSortedByDate.map((trend, index) => {
    const current = trend.value;
    const previous = previousTrendsSortedByDate?.[index]?.value;
    return {
      currentDate: trend.date,
      currentValue: getNumericValue(current) ?? 0,
      previousDate: previousTrendsSortedByDate?.[index]?.date,
      previousValue: getNumericValue(previous)
    };
  });
};

const getNumericValue = (
  value: string | number | undefined
): number | undefined => {
  if (
    value === undefined ||
    (typeof value === 'string' && value.length === 0)
  ) {
    return undefined;
  }
  if (typeof value === 'string') {
    return parseFloat(value);
  }
  return Number(value);
};

export const getChartLabelFromDate = (
  date: string,
  xAxisDateFormat: string
): string => {
  return dayjs(date).format(xAxisDateFormat);
};

export const getDescriptiveLabelFromDate = (date: string): string => {
  return dayjs(date).format(DATE_DESCRIPTIVE_FORMAT);
};

export const getFormattedValueByType = (
  value: number | string,
  type: EStatisticType
): string => {
  const numberValue = getNumericValue(value) ?? 0;
  const formatted = formatValue(numberValue, type);
  return typeof formatted === 'number' ? String(formatted) : formatted;
};

export const getDecimalPlaces = (value: number, type: EStatisticType) => {
  if (type === EStatisticType.NUMBER) {
    return 0;
  }

  return value % 1 !== 0 ? 2 : 0;
};

interface BreakdownValue {
  breakdownValue: string | number;
  value: number;
}

export const sortBreakdownData = <T extends BreakdownValue>(
  data: T[],
  sortConfig: BreakdownSortConfig
): T[] => {
  return [...data].sort((a, b) => {
    const { field, direction } = sortConfig;
    let comparison = 0;

    if (field === BreakdownSortField.VALUE) {
      comparison = a.value - b.value;
    } else if (field === BreakdownSortField.BREAKDOWN_VALUE) {
      comparison = String(a.breakdownValue).localeCompare(
        String(b.breakdownValue)
      );
    }

    return direction === SortingDirection.DESC ? -comparison : comparison;
  });
};

export const convertToPercentage = (
  value: Decimal | number | string
): number => {
  const numValue =
    value instanceof Decimal ? value.toNumber() : Number(value) || 0;
  return numValue <= 1 ? numValue * 100 : numValue;
};

export const convertToNumber = (value?: Decimal.Value) =>
  value instanceof Decimal ? value.toNumber() : Number(value) || 0;

export const formatValue = (
  value: number | string,
  type: EStatisticType
): string | number => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numericValue)) {
    return value;
  }

  if (type === EStatisticType.CURRENCY || type === EStatisticType.AMOUNT_AVERAGE) {
    return numericValue.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: numericValue % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    });
  }
  
  if (type === EStatisticType.PERCENTAGE) {
    return numericValue % 1 === 0 
      ? `${Math.round(numericValue)}%` 
      : `${numericValue.toFixed(1)}%`;
  }
  
  if (type === EStatisticType.NUMBER) {
    return numericValue % 1 === 0 ? Math.round(numericValue) : numericValue;
  }
  
  return numericValue;
};
